
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Category } from '@/types';
import { Label } from '@/components/ui/label';
import { CategorySelectOptions } from './CategorySelectOptions';

interface AddCategoryDialogProps {
  categories: Category[];
  onAddCategory: (name: string, parentId: string | null, type: 'Afdracht' | 'Geen afdracht') => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AddCategoryDialog = ({
  categories,
  onAddCategory,
  open,
  onOpenChange,
}: AddCategoryDialogProps) => {
  const [newCategoryName, setNewCategoryName] = useState('');
  const [selectedParentId, setSelectedParentId] = useState<string | null>(null);
  const [selectedType, setSelectedType] = useState<'Afdracht' | 'Geen afdracht'>('Geen afdracht');

  // Function to find parent category's type and level
  const getParentInfo = (parentId: string | null): { type: 'Afdracht' | 'Geen afdracht' | null, level: number } => {
    if (!parentId) return { type: null, level: 0 };
    
    const findInfo = (cats: Category[], currentLevel: number = 0): { type: 'Afdracht' | 'Geen afdracht' | null, level: number } => {
      for (const cat of cats) {
        if (cat.id === parentId) return { type: cat.type, level: currentLevel };
        const childResult = findInfo(cat.children, currentLevel + 1);
        if (childResult.type) return childResult;
      }
      return { type: null, level: 0 };
    };
    
    return findInfo(categories);
  };

  // Update selected type when parent changes
  useEffect(() => {
    const { type: parentType } = getParentInfo(selectedParentId);
    if (parentType) {
      setSelectedType(parentType);
    }
  }, [selectedParentId, categories]);

  const handleAddCategory = () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name');
      return;
    }

    const { level } = getParentInfo(selectedParentId);
    
    // Check if we're trying to add a category at level 5 or deeper
    if (level >= 4) {
      toast.error("Cannot add category: Maximum depth of 4 levels reached");
      return;
    }

    // If there's a parent, use its type, otherwise use the selected type
    const { type: parentType } = getParentInfo(selectedParentId);
    const typeToUse = selectedParentId ? parentType || selectedType : selectedType;
    
    onAddCategory(newCategoryName, selectedParentId, typeToUse);
    setNewCategoryName('');
    setSelectedParentId(null);
    setSelectedType('Geen afdracht');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Category</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="categoryName">Category Name</Label>
            <Input
              id="categoryName"
              placeholder="Enter the name of your category"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="parentCategory">Parent Category</Label>
            <select
              id="parentCategory"
              className="w-full border rounded-md p-2 bg-white dark:bg-gray-950"
              value={selectedParentId || ''}
              onChange={(e) => setSelectedParentId(e.target.value || null)}
            >
              <option value="" className="bg-gray-50 dark:bg-gray-900 py-1">Top Level Category</option>
              <CategorySelectOptions categories={categories} />
            </select>
            <p className="text-sm text-gray-500">Select a parent category or leave as top level</p>
          </div>
          {!selectedParentId && (
            <div className="space-y-2">
              <Label htmlFor="categoryType">Category Type</Label>
              <select
                id="categoryType"
                className="w-full border rounded-md p-2"
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as 'Afdracht' | 'Geen afdracht')}
              >
                <option value="Afdracht">Afdracht</option>
                <option value="Geen afdracht">Geen afdracht</option>
              </select>
              <p className="text-sm text-gray-500">Choose between Afdracht or Geen afdracht</p>
            </div>
          )}
          <Button 
            onClick={handleAddCategory} 
            className="w-full bg-[#ff6d00] hover:bg-[#ff6d00]/90 text-white"
          >
            Add Category
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
